import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { CRM_SUITE_OPTIONS, suiteLabel } from '@/lib/crmSuites';
import LocalizedDateInput from '@/components/shared/LocalizedDateInput';

const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];
const SOURCES = ['website_request', 'referral', 'cold_outreach', 'linkedin', 'existing_client', 'event', 'conference'];

const EMPTY = {
  company_name: '', primary_contact_name: '', job_title: '',
  country: '', industry: '', company_size: '', phone: '', email: '',
  pipeline_status: 'lead', estimated_value: '', next_action: '',
  follow_up_date: '', lead_source: '', notes: '',
  assigned_advisor_id: '',
};

export default function CreateCommercialLeadDialog({ open, onOpenChange, onCreated, advisors = [], isAdmin = false }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [suites, setSuites] = useState([]);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { if (open) { setForm(EMPTY); setSuites([]); } }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSuite = (code) => setSuites((s) => s.includes(code) ? s.filter((x) => x !== code) : [...s, code]);

  const submit = async () => {
    if (form.company_name.trim().length < 1 || form.primary_contact_name.trim().length < 1) {
      toast.error(t('pages.crm.dialog.requiredMissing', { defaultValue: 'Company name and contact name are required.' }));
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        company_name: form.company_name.trim(),
        primary_contact_name: form.primary_contact_name.trim(),
        estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
        interested_suites: suites,
      };
      // strip empty strings → undefined so backend treats as not set
      Object.keys(body).forEach((k) => { if (body[k] === '') delete body[k]; });
      await adminApi.createCommercialLead(body);
      toast.success(t('pages.crm.dialog.created', { defaultValue: 'Lead created' }));
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('pages.crm.dialog.createFailed', { defaultValue: 'Could not create lead' }), {
        description: formatApiError(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-crm-lead-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.crm.newLead', { defaultValue: 'New lead' })}</DialogTitle>
          <DialogDescription>
            {t('pages.crm.dialog.subtitle', { defaultValue: 'Capture a new commercial opportunity for the pipeline.' })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div>
            <Label htmlFor="cl-company">{t('pages.crm.field.companyName', { defaultValue: 'Company name' })} *</Label>
            <Input id="cl-company" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} data-testid="cl-company" />
          </div>
          <div>
            <Label htmlFor="cl-contact">{t('pages.crm.field.primaryContact', { defaultValue: 'Primary contact' })} *</Label>
            <Input id="cl-contact" value={form.primary_contact_name} onChange={(e) => update('primary_contact_name', e.target.value)} data-testid="cl-contact" />
          </div>
          <div>
            <Label>{t('pages.crm.field.jobTitle', { defaultValue: 'Job title' })}</Label>
            <Input value={form.job_title} onChange={(e) => update('job_title', e.target.value)} data-testid="cl-job-title" />
          </div>
          <div>
            <Label>{t('pages.crm.field.email', { defaultValue: 'Email' })}</Label>
            <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="cl-email" />
          </div>
          <div>
            <Label>{t('pages.crm.field.phone', { defaultValue: 'Phone' })}</Label>
            <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} data-testid="cl-phone" />
          </div>
          <div>
            <Label>{t('pages.crm.field.country', { defaultValue: 'Country' })}</Label>
            <Input value={form.country} onChange={(e) => update('country', e.target.value)} data-testid="cl-country" />
          </div>
          <div>
            <Label>{t('pages.crm.field.industry', { defaultValue: 'Industry' })}</Label>
            <Input value={form.industry} onChange={(e) => update('industry', e.target.value)} data-testid="cl-industry" />
          </div>
          <div>
            <Label>{t('pages.crm.field.companySize', { defaultValue: 'Company size' })}</Label>
            <Select value={form.company_size} onValueChange={(v) => update('company_size', v)}>
              <SelectTrigger data-testid="cl-size"><SelectValue placeholder={t('common.select', { defaultValue: 'Select' })} /></SelectTrigger>
              <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('pages.crm.field.estimatedValue', { defaultValue: 'Estimated opportunity value (USD)' })}</Label>
            <Input type="number" value={form.estimated_value} onChange={(e) => update('estimated_value', e.target.value)} data-testid="cl-value" />
          </div>
          <div>
            <Label>{t('pages.crm.field.followUp', { defaultValue: 'Follow-up date' })}</Label>
            <LocalizedDateInput value={form.follow_up_date} onChange={(v) => update('follow_up_date', v)} data-testid="cl-followup" />
          </div>
          <div>
            <Label>{t('pages.crm.field.source', { defaultValue: 'Lead source' })}</Label>
            <Select value={form.lead_source} onValueChange={(v) => update('lead_source', v)}>
              <SelectTrigger data-testid="cl-source"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{t(`pages.crm.source.${s}`, { defaultValue: s })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div>
              <Label>{t('pages.crm.field.advisor', { defaultValue: 'Assigned advisor' })}</Label>
              <Select value={form.assigned_advisor_id || 'unassigned'}
                      onValueChange={(v) => update('assigned_advisor_id', v === 'unassigned' ? '' : v)}>
                <SelectTrigger data-testid="cl-advisor"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">{t('pages.crm.unassigned', { defaultValue: 'Unassigned' })}</SelectItem>
                  {advisors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>{t('pages.crm.field.nextAction', { defaultValue: 'Next action' })}</Label>
            <Input value={form.next_action} onChange={(e) => update('next_action', e.target.value)} data-testid="cl-next" />
          </div>
          <div className="sm:col-span-2">
            <Label>{t('pages.crm.field.interestedSuites', { defaultValue: 'Interested suites' })}</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="cl-suites">
              {CRM_SUITE_OPTIONS.map((s) => (
                <button key={s} type="button"
                        onClick={() => toggleSuite(s)}
                        className={`px-2.5 py-1 rounded-full text-xs border transition ${suites.includes(s) ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-foreground border-border hover:bg-muted'}`}
                        data-testid={`cl-suite-${s}`}>
                  {suiteLabel(s)}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>{t('pages.crm.field.notes', { defaultValue: 'Notes' })}</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => update('notes', e.target.value)} data-testid="cl-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="cl-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-gradient-primary hover:opacity-90"
                  data-testid="cl-submit">
            {saving
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.saving', { defaultValue: 'Saving…' })}</>
              : t('pages.crm.dialog.create', { defaultValue: 'Create lead' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
